import { PublicKey } from '@solana/web3.js'
import { useQuery } from '@tanstack/react-query'
import store from 'stores/store'
import { AccountLayout } from '@solana/spl-token'
import { milliseconds } from 'date-fns'

export type TopHolder = {
  address: PublicKey
  amount: string
  decimals: number
  uiAmount: number
  uiAmountString: string
  owner: string | undefined
}

const getTokenAccountOwners = async (tokenAccountPubkeys: string[]) => {
  const connection = store.getState().connection
  const publicKeys = tokenAccountPubkeys.map((key) => new PublicKey(key))

  const accountsInfo = await connection.getMultipleAccountsInfo(publicKeys)

  const owners = accountsInfo.map((accountInfo, index) => {
    if (accountInfo === null) {
      console.warn(`Token account not found: ${tokenAccountPubkeys[index]}`)
      return null
    }

    const tokenAccountData = AccountLayout.decode(accountInfo.data)

    const ownerPubkey = new PublicKey(tokenAccountData.owner)

    return ownerPubkey.toString()
  })

  return owners
}

// const fetchTopHolders = async (mintAddress: string) => {
//   try {
//     const connection = store.getState().connection
//     const top20 = await connection.getTokenLargestAccounts(
//       new PublicKey(mintAddress),
//     )
//     if (top20?.value?.length) {
//       const holders = []
//       for (const holder of top20.value) {
//         const owner = await getTokenAccountOwner(holder.address)
//         holders.push({ ...holder, owner })
//       }
//       return holders as TopHolder[]
//     } else return []
//   } catch (e) {
//     console.error('failed to fetch top token holders', e)
//   }
// }

const fetchTopHolders = async (mintAddress: string): Promise<TopHolder[]> => {
  try {
    const connection = store.getState().connection
    const top20 = await connection.getTokenLargestAccounts(
      new PublicKey(mintAddress),
    )

    if (top20?.value?.length) {
      const tokenAccountPubkeys = top20.value.map((holder) =>
        holder.address.toString(),
      )

      const owners = await getTokenAccountOwners(tokenAccountPubkeys)

      const holders = top20.value.map((holder, index) => ({
        ...holder,
        owner: owners[index],
      }))

      return holders as TopHolder[]
    } else {
      return []
    }
  } catch (e) {
    console.error(
      `Failed to fetch top token holders: mintAddress=${mintAddress}`,
      e,
    )
    return []
  }
}

export function useTop20Holders(baseAddress: string | undefined) {
  return useQuery({
    queryKey: ['top-twenty-holders', baseAddress],
    queryFn: () => {
      if (!baseAddress) {
        throw new Error('Base address is required to fetch top holders')
      }
      return fetchTopHolders(baseAddress)
    },
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!baseAddress,
  })
}
